import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  Button,
  Input,
  Message,
  useToaster,
  SelectPicker,
  Stack,
  List,
  IconButton,
  Loader,
  InputNumber,
  Accordion
} from 'rsuite';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { addAccessProfile, addGroupWithPriority } from '@/services/accessProfile.service';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { Trash } from '@rsuite/icons';
import { RiUserLocationLine } from 'react-icons/ri';
import { getFloors } from '@/services/floor.service';
import { getZones } from '@/services/zone.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

const titleStyle = {
  fontSize: '1.1em',
  fontWeight: 600,
  color: '#333',
  margin: 0
};

const subTitleStyle = {
  fontSize: '1em',
  fontWeight: 500,
  color: '#666',
  marginLeft: 30
};

const daysOfWeek = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 }
];

interface AddPriorityDrawerProps {
  open: boolean;
  onClose: () => void;
}

const AddPriorityDrawer: React.FC<AddPriorityDrawerProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const toaster = useToaster();

  const [formValue, setFormValue] = useState({
    name: '',
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // const [zones, setZones] = useState([]);

  const floors = useAppSelector((state: any) => state.floor?.data || []);
  const zones = useAppSelector((state: any) => state.zone?.data)
    ?.map(item => {
      return {
        ...item,
        value: item._id,
        label: `${item.floor?.building?.address} / ${item.floor?.name} / ${item?.name}`
      };
    })
    ?.sort((a, b) => a.floor.floor_num - b.floor.floor_num);
  const loadingZones = useAppSelector((state: any) => state.zone?.loading);

  // const zones = useAppSelector((state: any) => {
  //   const allZones = state.zone?.data || [];

  //   // Map zones with additional properties
  //   const mappedZones = allZones.map(item => {
  //     const floorObj = floors.find(floor => floor.value === item.floor);
  //     return {
  //       ...item,
  //       value: item._id,
  //       label: `${floorObj?.building?.name} / ${floorObj?.label || 'Unknown Floor'} / ${item.name}`,
  //       floor: item.floor,
  //       sortOrder: floorObj?.sortOrder || 0
  //     };
  //   });

  //   return mappedZones.sort((a, b) => a.sortOrder - b.sortOrder);
  // });

  const [localAccessList, setLocalAccessList] = useState([
    {
      zone: null,
      data: [
        {
          dayOfWeek: null,
          priority: null
        }
      ]
    }
  ]);

  // useEffect(() => {
  //   if (allZones?.length > 0 && floors.length > 0) {
  //     const mappedZones = allZones.map(item => {
  //       const floorObj = floors.find(floor => floor._id === item.floor);
  //       return {
  //         ...item,
  //         value: item._id,
  //         label: `${floorObj?.building?.address || 'building'} / ${
  //           floorObj?.name || 'Unknown Floor'
  //         } / ${item.name}`,
  //         floor: item.floor,
  //         sortOrder: floorObj?.sortOrder || 0
  //       };
  //     });

  //     setZones(mappedZones.sort((a, b) => a.sortOrder - b.sortOrder));
  //   }
  // }, [allZones, floors]);

  useEffect(() => {
    const initData = async () => {
      try {
        setLoading(true);
        await Promise.all([dispatch(getFloors()), dispatch(getZones())]);
      } catch (error) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            Failed to initialize form data
          </Message>,
          { placement: 'topCenter' }
        );
      } finally {
        setLoading(false);
      }
    };

    if (open) initData();
  }, [open, dispatch]);

  const handleAddZoneRow = () => {
    setLocalAccessList([
      ...localAccessList,
      {
        zone: null,
        data: [
          {
            dayOfWeek: null,
            priority: null
          }
        ]
      }
    ]);
  };

  const handleAddRow = (indexRow: number) => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        return {
          ...row,
          data: [
            ...row.data,
            {
              dayOfWeek: null,
              priority: null
            }
          ]
        };
      })
    );
  };

  const handleChange = (indexRow: number, key: string, value: any, index: number | null = null) => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        if (key === 'zone') {
          return {
            ...row,
            zone: value,
            data: daysOfWeek.map(day => ({
              dayOfWeek: day.value,
              priority: 1 // Default priority
            }))
          };
        }

        if (index === null) {
          return { ...row, [key]: value };
        } else {
          const updatedData = row.data.map((item, itemIndex) =>
            itemIndex === index ? { ...item, [key]: value } : item
          );
          return { ...row, data: updatedData };
        }
      })
    );
  };

  const handleDeleteZoneRow = (indexRow: number) => {
    setLocalAccessList(prevList => prevList.filter((_, rowIndex) => rowIndex !== indexRow));
  };

  const handleRemoveRow = (indexRow: number, index: number) => {
    // Prevent removing the last row
    if (localAccessList[indexRow].data.length === 1) {
      return;
    }

    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        const updatedData = row.data.filter((_, itemIndex) => itemIndex !== index);
        return { ...row, data: updatedData };
      })
    );
  };

  const handleSubmit = async () => {
    try {
      if (!formValue.name || localAccessList.some(item => !item.zone)) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            Please fill in all required fields
          </Message>,
          { placement: 'topCenter' }
        );
        return;
      }

      const invalidZones = localAccessList.some(
        item => !item.data || item.data.some(d => !d.dayOfWeek || d.priority === null)
      );
      if (invalidZones) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            Please fill in all day and priority values
          </Message>,
          { placement: 'topCenter' }
        );
        return;
      }

      setIsSaving(true);
      const zones = localAccessList.map(item => item.zone);
      const priorityConfigs = localAccessList.flatMap(({ zone, data }) =>
        data.map(({ dayOfWeek, priority }) => ({
          zone,
          dayOfWeek: parseInt(dayOfWeek),
          priority: parseInt(priority)
        }))
      );

      const groupData = {
        name: formValue.name,
        description: formValue.description,
        zones,
        priorityConfigs
      };

      const result = await dispatch(addGroupWithPriority(groupData));
      if (result?.payload?.group) {
        toaster.push(
          <Message type="success" duration={5000} showIcon>
            {t('Profile created successfully')}
          </Message>,
          { placement: 'topCenter' }
        );

        setFormValue({ name: '', description: '' });
        setLocalAccessList([
          {
            zone: null,
            data: [{ dayOfWeek: null, priority: null }]
          }
        ]);

        onClose();
      } else {
        throw new Error('Failed to create group with priorities');
      }
    } catch (error: any) {
      console.error('Submission error:', error);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message) || t('Error creating profile')}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <Loader center content="Loading..." />;
  const { t } = useTranslation();

  return (
    <Drawer backdrop="static" size="md" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Add Access Profile with Priority')}</Drawer.Title>
        <Drawer.Actions>
          <Button
            onClick={handleSubmit}
            appearance="primary"
            loading={isSaving}
            disabled={isSaving}
          >
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle" disabled={isSaving}>
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('Name *')}</Form.ControlLabel>
            <Form.Control name="name" placeholder="Enter profile name" required />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Description')}</Form.ControlLabel>
            <Form.Control
              name="description"
              placeholder="Enter description"
              accepter={Input as any}
            />
          </Form.Group>

          <Stack
            style={{
              marginTop: 50,
              marginBottom: 20,
              marginLeft: 10
            }}
            spacing={10}
            alignItems="center"
          >
            <RiUserLocationLine size={20} />
            <h6 style={titleStyle}>{t('Manage accesses with priority')}</h6>
          </Stack>

          <div className="ml-10">
            <Accordion defaultActiveKey={0}>
              {localAccessList.map((row, indexRow) => (
                <Accordion.Panel
                  key={indexRow}
                  header={
                    <Stack spacing={10} alignItems="flex-start">
                      {row.zone ? (
                        <span style={subTitleStyle}>
                          {zones.find(z => z._id === row.zone)?.label || 'No zone selected'}
                        </span>
                      ) : (
                        <span style={subTitleStyle}>{t('New Zone Access')}</span>
                      )}
                    </Stack>
                  }
                  defaultExpanded
                >
                  <Stack justifyContent="space-between">
                    <div style={{ textAlign: 'left', marginLeft: '28px' }}>
                      <div style={slimText}>{'Zone *'}</div>
                      <SelectPicker
                        data={zones}
                        value={row.zone}
                        onChange={value => handleChange(indexRow, 'zone', value)}
                        style={{ width: 250 }}
                        placement="auto"
                        disabledItemValues={localAccessList
                          .filter((_, i) => i !== indexRow)
                          .map(item => item.zone)
                          .filter(Boolean)}
                        cleanable={false}
                        placeholder={t('Select Zone')}
                        loading={loadingZones}
                      />
                    </div>
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<Trash color="red" />}
                      onClick={() => handleDeleteZoneRow(indexRow)}
                      disabled={localAccessList.length === 1}
                    />
                  </Stack>

                  <List style={{ marginLeft: 50, marginTop: 20 }}>
                    {row.data.map((item, index) => (
                      <List.Item key={index}>
                        <Stack spacing={20} wrap>
                          <div style={{ textAlign: 'left' }}>
                            <div style={slimText}>{t('Day of week *')}</div>
                            <SelectPicker
                              data={daysOfWeek}
                              value={item.dayOfWeek}
                              onChange={value => handleChange(indexRow, 'dayOfWeek', value, index)}
                              style={{ width: 250 }}
                              placement="auto"
                              disabledItemValues={row.data
                                .filter((_, i) => i !== index)
                                .map(item => item.dayOfWeek)
                                .filter(Boolean)}
                              placeholder={t('Select Day')}
                            />
                          </div>

                          <div style={{ textAlign: 'left' }}>
                            <div style={slimText}>{t('Priority *')}</div>
                            <InputNumber
                              value={item.priority}
                              onChange={value => handleChange(indexRow, 'priority', value, index)}
                              style={{ width: 100 }}
                              min={1}
                            />
                          </div>

                          <Stack spacing={5} alignItems="flex-end">
                            <IconButton
                              onClick={() => handleRemoveRow(indexRow, index)}
                              icon={<MinusIcon />}
                              size="xs"
                              disabled={row.data.length === 1}
                            />
                            {index === row.data.length - 1 && (
                              <IconButton
                                onClick={() => handleAddRow(indexRow)}
                                icon={<PlusIcon />}
                                size="xs"
                              />
                            )}
                          </Stack>
                        </Stack>
                      </List.Item>
                    ))}
                  </List>
                </Accordion.Panel>
              ))}
            </Accordion>

            <IconButton
              onClick={handleAddZoneRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default AddPriorityDrawer;
